import React, { useCallback, useState } from 'react';
import Icon from 'components/Utils/NewIcon';
import { MarkRead } from 'hoc/markUnread';

/**
 * Wraps a Carey-artifact record badge with an inline expand/collapse affordance.
 *
 * Instead of navigating away when the patient taps a badge, the detail view (the same one the
 * records page shows) unfolds inline. On first expansion, the record is marked as read via the
 * existing `<MarkRead>` HOC — no extra wiring per record type.
 *
 * Props:
 * - renderBadge({ toggle, expanded }): the badge to use as the collapsed header. The caller wires
 *   the badge's click handler to `toggle`.
 * - renderDetail(): returns the expanded detail JSX. Only called once the patient has expanded
 *   at least once — so the detail components' heavy `useEffect`s (e.g. PatientAppointmentDetails
 *   fetching booking-flow options + video status) don't fire until needed.
 * - markType: one of MarkHealthRecordReadTypeEnum ('Appointment' | 'AppointmentNote' |
 *   'Immunization' | 'Prescription' | 'PatientDocument'). Passed through to `<MarkRead>`.
 * - record: the record object (must have `id`, `patient_id`, `read`). Required by `<MarkRead>`.
 */
const InlineRecordExpander = ({ renderBadge, renderDetail, markType, record }) => {
  const [expanded, setExpanded] = useState(false);
  // Once the patient has expanded at least once we keep the detail mounted, so subsequent
  // collapses/expands don't re-fetch anything inside the detail components — and MarkRead's
  // useEffect doesn't re-fire (it only runs on first mount of the wrapped subtree).
  const [hasExpanded, setHasExpanded] = useState(false);

  const toggle = useCallback(() => {
    setExpanded((prev) => {
      if (!prev) setHasExpanded(true);
      return !prev;
    });
  }, []);

  const className = ['carey-records__expander', expanded && 'is-expanded']
    .filter(Boolean)
    .join(' ');

  return (
    <div className={className}>
      {renderBadge({ toggle, expanded })}
      <button
        type="button"
        className="carey-records__expander-toggle"
        onClick={toggle}
        aria-expanded={expanded}
      >
        <span className="carey-records__expander-label">
          {expanded ? 'Hide details' : 'Show details'}
        </span>
        <Icon
          src={['far', 'chevron-down']}
          width="12px"
          height="12px"
          className="carey-records__expander-chevron"
        />
      </button>
      {hasExpanded && (
        <div className="carey-records__expander-content" aria-hidden={!expanded}>
          <MarkRead type={markType} item={record}>
            {renderDetail()}
          </MarkRead>
        </div>
      )}
    </div>
  );
};

export default InlineRecordExpander;
